import {useEffect, useRef, useState} from 'react'

const THROTTLE_INTERVAL_MS = 200

/**
 * Shows results while the user types, instead of waiting for a pause.
 * The first keystroke triggers immediately, then we limit updates to every
 * 200ms. We always send the latest value so nothing gets lost.
 *
 * Setting the query to null (closing the menu) still happens instantly.
 */
export function useThrottledFilterQuery<T>(query: T | null) {
  const [throttledQuery, setThrottledQuery] = useState<T | null>(null)
  const lastUpdateAtRef = useRef(0)
  const trailingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Open and close the menu without delay.
  if ((throttledQuery === null && query !== null) || (throttledQuery !== null && query === null)) {
    setThrottledQuery(query)
  }

  useEffect(() => {
    if (query === null) return

    const elapsed = Date.now() - lastUpdateAtRef.current

    if (elapsed >= THROTTLE_INTERVAL_MS) {
      lastUpdateAtRef.current = Date.now()
      setThrottledQuery(query)
      return
    }

    // Schedule a final update so the last keystroke is not dropped.
    trailingTimeoutRef.current = setTimeout(() => {
      lastUpdateAtRef.current = Date.now()
      trailingTimeoutRef.current = null
      setThrottledQuery(query)
    }, THROTTLE_INTERVAL_MS - elapsed)

    return () => {
      if (trailingTimeoutRef.current) {
        clearTimeout(trailingTimeoutRef.current)
        trailingTimeoutRef.current = null
      }
    }
  }, [query])

  return throttledQuery
}
